import { ReactElement, useMemo, useState } from 'react'
import {
    MaterialReactTable,
    MRT_ColumnDef,
    useMaterialReactTable,
} from 'material-react-table'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { useAppDispatch, useAppSelector } from '../../../redux/hook'
import { useCreateRule } from './hooks/createRule'
import { useDeleteRule } from './hooks/deleteRule'
import { useUpdateRule } from './hooks/updateRule'
import ConfirmationDialog from '../../Common/ConfirmationDialog'
import RuelInstance from './RuleInstance'
import { RulesColumns } from '../../../services/apiInterfaces'
import RulesTemplateList from './RulesTemplateList'
import { updateValuesForPayload } from '../../../utils/EntityAttributesUtils'
import { addRule, updateRule } from '../../../services/dtServices'
import { updateLoader } from '../../../redux/slices/loaderSlice'
import { useTranslation } from 'react-i18next'
import { ruleAttributesToExclude } from '../../../constants/constants'
import { RuleDefinition } from '../interfaces'
import TruncatedText from '../../Common/TruncatedText'
import { getDisplayNameFromTargetAttribute } from '../../../utils/MiscellaneousUtils'

interface RuleSetProps {
    id: string
    name: string
    rules: any
    readOnly: boolean
    columns: RulesColumns[]
    setReload: React.Dispatch<React.SetStateAction<boolean>>
}

const RuleSet = (props: RuleSetProps): ReactElement => {
    const { t } = useTranslation()
    const { rules: data, id, name, readOnly, columns, setReload } = props

    const org = useAppSelector((state) => state.org.org)
    const selectedEntity = useAppSelector(
        (state) => state.dtStudio.selectedEntity,
    )

    const dispatch = useAppDispatch()
    const { id: entityId } = selectedEntity
    const orgId = org?.id ? org?.id : ''

    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
    const [ruleIdToDelete, setRuleIdToDelete] = useState(null)

    //new rule instannce
    const [openFields, setOpenFields] = useState<boolean>(false)
    const [selectedRuleId, setSelectedRuleId] = useState<string>('')

    const [openSubTemplates, setOpenSubTemplates] = useState<boolean>(false)
    const [createTemplateId, setCreateTemplateId] = useState<string>('')

    //preview the rule
    const [isPreview, setIsPreview] = useState<boolean>(false)
    const [ruleTypeName, setRuleTypeName] = useState<string>('')

    const ruleNameColumnName = columns[0]?.columnName || 'Rule Name'

    const rulesColumns: MRT_ColumnDef<RuleDefinition>[] = useMemo(
        () => [
            {
                accessorKey: ruleNameColumnName,
                header: ruleNameColumnName,
                size: 100,
                Cell: ({ cell }) => (
                    <TruncatedText text={cell.getValue() as string} />
                ),
            },
            {
                accessorKey: 'ruleTemplateName',
                header: t('ruleType'),
                size: 100,
                Cell: ({ cell }) => (
                    <TruncatedText text={(cell.getValue() as string) || ''} />
                ),
            },
            {
                accessorKey: columns[1]?.attributeName
                    ? `${columns[1].attributeName}::S`
                    : 'targetAttribute::S',
                header: columns[1]?.columnName || t('Target Attribute'),
                size: 100,
                Cell: ({ cell }) => (
                    <TruncatedText
                        text={getDisplayNameFromTargetAttribute(
                            cell.getValue() as string,
                        )}
                    />
                ),
            },
            {
                header: t('ruleSummary'),
                dataType: 'string',
                Cell: ({ row }) => {
                    let summary = ''
                    Object.keys(row.original).forEach((key) => {
                        if (
                            !ruleAttributesToExclude.includes(key) &&
                            !key.endsWith('::S') &&
                            row.original[key] !== null
                        ) {
                            summary += `${key}: ${row.original[key]}, `
                        }
                    })
                    const finalSummary = summary.slice(0, -2)
                    return <TruncatedText text={finalSummary} />
                },
            },
        ],
        [columns, t],
    )

    // call CREATE hook
    const { isPending: isCreatingRule } = useCreateRule(orgId, id, entityId)

    // call UPDATE hook
    const { isPending: isUpdatingRule } = useUpdateRule(orgId, id, entityId)

    // call DELETE hook
    const { mutateAsync: deleteRule, isPending: isDeletingRule } =
        useDeleteRule(orgId, id, entityId)

    // DELETE action
    const openDeleteConfirmModal = (row: any) => {
        const ruleId = row.original.ruleId || ''
        setRuleIdToDelete(ruleId)
        setOpenDeleteDialog(true)
    }

    const handleDeleteDialogOperation = () => {
        const deletedID = ruleIdToDelete ?? ''
        deleteRule(deletedID)
        setOpenDeleteDialog(false)
    }

    //new Create Instance

    const onCloseSubTemplatePopUp = () => {
        setOpenSubTemplates(false)
    }

    const onSubmitSubTemplatePopUp = (val: string, templateName: string) => {
        setOpenSubTemplates(false)
        setCreateTemplateId(val)
        setOpenFields(true)
        setIsPreview(false)
        setRuleTypeName(templateName)
    }

    const handleAddNewRule = () => {
        setSelectedRuleId('')
        setOpenSubTemplates(true)
    }

    const handleCloseFields = () => {
        setOpenFields(false)
        setSelectedRuleId('')
        setIsPreview(false)
        setRuleTypeName('')
    }

    const onEditRule = (row: any) => {
        const ruleId = row.original.ruleId
        const rowTemplateId = row.original.ruleTemplateId || ''
        setSelectedRuleId(ruleId)
        setOpenFields(true)
        setIsPreview(false)
        setCreateTemplateId(rowTemplateId)
        setRuleTypeName(row.original.ruleTemplateName || '')
    }

    const onPreview = (row: any) => {
        const ruleId = row.original.ruleId
        const rowTemplateId = row.original.ruleTemplateId || ''
        setSelectedRuleId(ruleId)
        setOpenFields(true)
        setCreateTemplateId(rowTemplateId)
        setIsPreview(true)
        setRuleTypeName(row.original.ruleTemplateName || '')
    }

    const onSave = async (fileds: any[]) => {
        const updatedFields = updateValuesForPayload(fileds)

        const rulesPayload: any = {
            attributeValues: {},
            entityTemplateId: createTemplateId,
        }

        updatedFields.forEach((i: any) => {
            rulesPayload.attributeValues[i.id] = i.value
        })
        let ruleResult: any = ''
        dispatch(updateLoader(true))
        if (selectedRuleId) {
            rulesPayload.attributeValues['rule@id'] = selectedRuleId
            ruleResult = await updateRule(org?.id || '', rulesPayload)
        } else {
            ruleResult = await addRule(org?.id || '', id, rulesPayload)
        }
        dispatch(updateLoader(false))

        if (ruleResult) {
            setOpenFields(false)
            setIsPreview(false)
            setSelectedRuleId('')
            setCreateTemplateId('')
            setReload((prev) => !prev)
        }
    }

    const table = useMaterialReactTable({
        columns: rulesColumns || [],
        data,
        createDisplayMode: 'row',
        editDisplayMode: 'row',
        enableEditing: true,
        positionActionsColumn: 'last',
        enableColumnActions: false,
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enableHiding: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableSorting: false,
        enableTopToolbar: false,
        enablePagination: false,
        positionCreatingRow: 'bottom',
        layoutMode: 'grid',
        displayColumnDefOptions: {
            'mrt-row-actions': { header: '', size: 80 },
        },
        muiTableBodyCellProps: {
            sx: {
                border: 'none',
                fontSize: '14px',
            },
        },
        muiTableHeadCellProps: {
            sx: {
                border: 'none',
                fontSize: '14px',
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: 'none',
            },
        },
        muiBottomToolbarProps: {
            sx: {
                boxShadow: 'none',
            },
        },
        renderRowActions: ({ row }) =>
            readOnly ? (
                <Box
                    sx={{ display: 'flex', flexWrap: 'nowrap', width: '100px' }}
                >
                    <IconButton
                        onClick={() => {
                            onPreview(row)
                        }}
                        aria-haspopup="true"
                        sx={{
                            height: '20px',
                        }}
                        id={
                            name +
                            '-' +
                            row.original[ruleNameColumnName] +
                            '-preview-business-rule-button'
                        }
                    >
                        <img
                            src="/assets/images/icons/preview_plan.svg"
                            alt={`${name + '-' + row.original[ruleNameColumnName]}-preview`}
                        />
                    </IconButton>
                </Box>
            ) : (
                <Box
                    sx={{ display: 'flex', flexWrap: 'nowrap', width: '100px' }}
                >
                    <Tooltip title={t('edit')}>
                        <IconButton
                            id={
                                name +
                                '-' +
                                row.original[ruleNameColumnName] +
                                '-edit-rule-button'
                            }
                            onClick={() => {
                                onEditRule(row)
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        id={
                            name +
                            '-' +
                            row.original[ruleNameColumnName] +
                            '-delete-rule-button'
                        }
                        color="error"
                        onClick={() => openDeleteConfirmModal(row)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
        enableBottomToolbar: !readOnly,
        renderBottomToolbarCustomActions: () => (
            <Button
                id={name + '-add-new-rule-button'}
                variant="contained"
                onClick={() => {
                    handleAddNewRule()
                }}
            >
                {t('addNewRule')}
            </Button>
        ),
        muiEditRowDialogProps: {
            open: true,
            maxWidth: 'md',
            sx: {
                padding: '20px !important',
            },
        },
        state: {
            isSaving: isCreatingRule || isUpdatingRule || isDeletingRule,
            density: 'compact',
        },
    })

    return (
        <>
            <MaterialReactTable table={table} />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={() => handleDeleteDialogOperation()}
                handleClose={() => setOpenDeleteDialog(false)}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('confirmDeleteRule')}
            />
            <RuelInstance
                instanceId={selectedRuleId || ''}
                createTemplateId={createTemplateId || ''}
                onSave={onSave}
                openFields={openFields}
                handleCloseFields={handleCloseFields}
                isPreview={isPreview}
                ruleTypeName={ruleTypeName}
            />
            <RulesTemplateList
                isOpen={openSubTemplates}
                onClose={onCloseSubTemplatePopUp}
                onSubmit={onSubmitSubTemplatePopUp}
            />
        </>
    )
}

export default RuleSet
